/* eslint-disable no-plusplus */
declare const google: any;
import React, { useEffect, useState, FC } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import moneyFormat from 'views/rebranded-module/common/utils/moneyFormat';
import Dinero from 'views/rebranded-module/common/utils/Dinero';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import { GeoMarker } from 'views/rebranded-module/GeoViewV2/components';
import getUrlQuery from 'lib/utils/getUrlQuery';
import { useDispatch } from 'react-redux';
import { setSnackbar } from 'redux/features/ui-slice';
import { useContent } from 'views/rebranded-module/common/context/useContent';
import axios, { API } from 'lib/config/axios';

interface RentalItem {
  id: any;
  image: {
    src: string;
    alt: string;
  };
  title: string;
  subTitle: string;
  price: string;
}

interface MapLocation {
  id: number;
  lat: number | string;
  lng: number | string;
  slug: string;
  display_name: string;
  vehicles_count: number;
}

interface MapCardData {
  open: boolean;
  loading?: boolean;
  rentalsItems?: RentalItem[];
}

const useMapLogic = (props) => {
  const theme = useTheme();
  // isMapLoading: passed from GeoViewV2, indicates a "Search this area" API request
  // is in-flight. Forwarded to GeoMarker as `disabled` to prevent pin clicks during loading.
  const { mapLocations, selectedPlace, hoveredBike, currentFilterParams, isMapLoading, handleMapLoadingFlag } = props;
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const appNavigator = useAppNavigator();
  const dispatch = useDispatch();
  const cmsData = useContent();
  const [mapCardData, setMapCardData] = useState<MapCardData>({ open: false });
  const [mapContolObj, setMapContolObj] = useState<any>();
  const [fittingBounds, setFittingBounds] = useState(true);
  const { lat, lng, radius } = getUrlQuery();
  const { lat: aNLat, lng: aNLng, radius: aNRadius } = appNavigator.query;

  const isUsingInteractiveMap = () => (lat || aNLat) && (lng || aNLng) && (radius || aNRadius) && mapLocations;

  const calculateMapCenter = (mapObj) => {
    if (typeof mapObj !== 'object') return;
    if (!mapLocations) return;
    if (mapLocations.length > 1) {
      // eslint-disable-next-line no-undef
      const bound = new google.maps.LatLngBounds();
      for (let i = 0; i < mapLocations.length; i++) {
        bound.extend(
          // eslint-disable-next-line no-undef
          new google.maps.LatLng(
            mapLocations[i].lat,
            mapLocations[i].lng,
          ),
        );
      }
      mapObj.fitBounds(bound);
    } else if (mapLocations.length == 1) {
      mapObj.setCenter({
        lat: parseFloat(mapLocations[0].lat),
        lng: parseFloat(mapLocations[0].lng),
      });
      mapObj.setZoom(6);
    } else if (mapLocations.length == 0) {
      if (selectedPlace) {
        if (selectedPlace.location.lat && selectedPlace.location.lng) {
          mapObj.setCenter({
            lat: parseFloat(selectedPlace.location.lat),
            lng: parseFloat(selectedPlace.location.lng),
          });
          mapObj.setZoom(6);
        } else {
          mapObj.setCenter({
            lat: 16.213487,
            lng: -35.759664,
          });
          mapObj.setZoom(0);
        }
      }
    }
  };

  const handleGoogleMapLoaded = (map) => {
    const showMapFailedError = () => dispatch(
      setSnackbar({
        open: true,
        severity: 'error',
        message: cmsData.GeoPage.mapFailedLabel,
      }),
    );

    if (!map) {
      showMapFailedError();
    } else {
      try {
        calculateMapCenter(map);
        setMapContolObj(map);
      } catch (e) {
        showMapFailedError();
      }
    }
  };

  const handleMarkerClick = async (location: MapLocation) => {
    setMapCardData({ open: true, loading: true, rentalsItems: [] });
    try {
      const res = await axios.get(API.mapLocationRentals, {
        params: { ...currentFilterParams, map_location_id: location.id },
      });
      const rentalsItems = (res.data.rentals || []).map((rental: any) => ({
        ...rental,
        id: rental.id,
        image: {
          src: rental.image_url || 'imgs/Rebranding/logos/default.jpg',
          alt: `EAGLERIDER ${rental.vehicle_make.name} ${rental.vehicle_model.name}`,
        },
        title: rental.vehicle_make.name,
        subTitle: rental.vehicle_model.name,
        price: moneyFormat(Dinero(rental.price.rate_per_day)),
      }));
      setMapCardData({ open: true, loading: false, rentalsItems });
    } catch {
      setMapCardData({ open: false });
    }
  };

  const handleMapCardClose = () => {
    setMapCardData({ open: false });
  };

  // disabled={isMapLoading}: when a "Search this area" request is in-flight,
  // all map pins become non-interactive (faded out, pointer-events: none)
  // to prevent the user from selecting a vehicle that may disappear once
  // new results load in. See GeoMarker.tsx and GeoMarker.styles.tsx for the
  // visual treatment (GMDisabled class).
  const renderMapMarkers = () => mapLocations?.map((location: MapLocation) => (
    <GeoMarker
      key={location.id}
      lat={+location.lat}
      lng={+location.lng}
      location={location}
      handleMarkerClick={handleMarkerClick}
      hoveredBike={hoveredBike}
      disabled={isMapLoading}
    />
  ));

  useEffect(() => {
    if (!mapLocations) return;
    handleMapCardClose();
    handleMapLoadingFlag(false);
  }, [mapLocations]);

  useEffect(() => {
    mapContolObj?.addListener('idle', () => {
      setFittingBounds(false);
    });

    if (isUsingInteractiveMap()) return;

    if (mapLocations && mapContolObj) {
      setFittingBounds(true);
      calculateMapCenter(mapContolObj);
    }
  }, [mapLocations, mapContolObj]);

  return {
    mapCardData,
    renderMapMarkers,
    handleGoogleMapLoaded,
    isTablet,
    handleMapCardClose,
    fittingBounds,
    setFittingBounds,
  };
};

export default useMapLogic;
